/**
 * Servicio para gestión de usuarios
 * Sistema Klever Octopus - Betel Natural
 */
import apiClient, { handleApiError, formatApiResponse } from './api';

// Endpoints para usuarios
const ENDPOINTS = {
  USERS: '/users',
  USER_BY_ID: (id) => `/users/${id}`,
  USER_PERMISSIONS: (id) => `/users/${id}/permissions`,
  USER_ROLES: '/users/roles',
  ALL_PERMISSIONS: '/users/permissions',
  USER_STATUS: (id) => `/users/${id}/toggle-status`,
  USER_PASSWORD: (id) => `/users/${id}/reset-password`,
  USER_STATS: '/users/stats'
};

// Roles RBAC (nombre) -> enum legacy de UserRole que el backend usa para autorización
// (require_admin / require_manager validan el enum `role`, no los role_ids).
const ROLE_NAME_TO_ENUM = {
  superadmin: 'superuser',
  superuser: 'superuser',
  administrator: 'admin',
  admin: 'admin',
  administrador: 'admin',
  manager: 'manager',
  gerente: 'manager',
  sales_representative: 'sales',
  sales: 'sales',
  vendedor: 'sales',
  support: 'support',
  soporte: 'support',
  client: 'client',
  cliente: 'client'
};

/**
 * Deriva el enum legacy de UserRole a partir del rol RBAC seleccionado.
 * Primero intenta por nombre; si es un rol personalizado, usa el nivel jerárquico (0-100).
 */
const mapRoleToEnum = (roleName, roleLevel) => {
  const byName = roleName ? ROLE_NAME_TO_ENUM[String(roleName).toLowerCase()] : undefined;
  if (byName) return byName;

  const level = Number(roleLevel) || 0;
  if (level >= 90) return 'admin';
  if (level >= 50) return 'manager';
  if (level >= 10) return 'sales';
  return 'support';
};

/**
 * Obtener lista de usuarios con paginación y filtros
 */
export const getUsers = async (params = {}) => {
  try {
    const queryParams = {
      skip: params.skip || ((params.page - 1) * params.per_page) || 0,
      limit: params.limit || params.per_page || 20,
      search: params.search || undefined,
      // El filtro de rol usa el ID del rol RBAC (no el enum legacy)
      role_id: params.role || undefined,
      is_active: params.status === 'active' ? true : params.status === 'inactive' ? false : undefined,
      sort_by: params.sort_by || undefined,
      sort_order: params.sort_order || undefined
    };

    // Remover parámetros undefined
    Object.keys(queryParams).forEach(key => queryParams[key] === undefined && delete queryParams[key]);

    const response = await apiClient.get(ENDPOINTS.USERS, { params: queryParams });
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Obtener usuario por ID
 */
export const getUserById = async (id) => {
  try {
    const response = await apiClient.get(ENDPOINTS.USER_BY_ID(id));
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Crear nuevo usuario
 */
export const createUser = async (userData) => {
  try {
    // El backend usa el enum legacy `role` para autorización y `role_ids` para RBAC
    const roleEnum = mapRoleToEnum(userData.role_name, userData.role_level);

    const payload = {
      email: userData.email,
      password: userData.password,
      full_name: `${userData.nombre} ${userData.apellido}`,
      role: roleEnum,
      role_ids: userData.role_id ? [parseInt(userData.role_id)] : undefined,
      phone: userData.telefono || undefined,
      address: userData.address || undefined,
      timezone: userData.timezone || 'America/Bogota',
      language: userData.language || 'es'
    };

    // Campos específicos para vendedores
    if (roleEnum === 'sales') {
      payload.commission_percentage = userData.comision_porcentaje || 0;
      payload.payment_period = userData.tipo_pago === 'mensual' ? 'MONTHLY' :
                               userData.tipo_pago === 'quincenal' ? 'BIWEEKLY' :
                               userData.tipo_pago === 'semanal' ? 'WEEKLY' : 'MONTHLY';
      payload.payment_method = userData.forma_pago === 'deposito' ? 'BANK_TRANSFER' :
                               userData.forma_pago === 'efectivo' ? 'CASH' : 'BANK_TRANSFER';
      payload.bank_name = userData.datos_bancarios?.banco || undefined;
      payload.bank_account = userData.datos_bancarios?.numero_cuenta || undefined;
    }

    // Remover valores undefined
    Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

    const response = await apiClient.post(ENDPOINTS.USERS, payload);
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Actualizar usuario
 */
export const updateUser = async (id, userData) => {
  try {
    const payload = {
      full_name: `${userData.nombre} ${userData.apellido}`,
      phone: userData.telefono || undefined,
      role_ids: userData.role_id ? [parseInt(userData.role_id)] : undefined, // Enviar como array de IDs
      is_active: userData.is_active,
      address: userData.address || undefined,
      timezone: userData.timezone || undefined,
      language: userData.language || undefined
    };

    // Campos específicos para vendedores
    if (userData.comision_porcentaje || userData.tipo_pago || userData.forma_pago) {
      payload.commission_percentage = userData.comision_porcentaje || 0;
      payload.payment_period = userData.tipo_pago === 'mensual' ? 'MONTHLY' :
                               userData.tipo_pago === 'quincenal' ? 'BIWEEKLY' :
                               userData.tipo_pago === 'semanal' ? 'WEEKLY' : 'MONTHLY';
      payload.payment_method = userData.forma_pago === 'deposito' ? 'BANK_TRANSFER' :
                               userData.forma_pago === 'efectivo' ? 'CASH' : 'BANK_TRANSFER';
      payload.bank_name = userData.datos_bancarios?.banco || undefined;
      payload.bank_account = userData.datos_bancarios?.numero_cuenta || undefined;
    }

    // Remover valores undefined
    Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

    const response = await apiClient.put(ENDPOINTS.USER_BY_ID(id), payload);

    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Eliminar usuario (soft delete)
 */
export const deleteUser = async (id) => {
  try {
    const response = await apiClient.delete(ENDPOINTS.USER_BY_ID(id));
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Cambiar estado del usuario (activar/desactivar)
 */
export const changeUserStatus = async (id, isActive) => {
  try {
    const response = await apiClient.post(ENDPOINTS.USER_STATUS(id));
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Cambiar contraseña del usuario
 */
export const changeUserPassword = async (id, passwordData) => {
  try {
    const payload = {
      new_password: passwordData.newPassword,
      // Forzar cambio en el próximo inicio de sesión del usuario
      force_change: passwordData.forceChange || false
    };

    // Solo enviar la contraseña actual si se proporcionó (no aplica al forzar el cambio)
    if (passwordData.currentPassword) {
      payload.current_password = passwordData.currentPassword;
    }

    const response = await apiClient.post(ENDPOINTS.USER_PASSWORD(id), payload);
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Cambiar la contraseña del propio usuario autenticado.
 * El backend limpia la obligación de cambio (must_change_password) al hacerlo.
 */
export const changeMyPassword = async (currentPassword, newPassword) => {
  try {
    const response = await apiClient.post('/users/me/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    });
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Obtener permisos de un usuario
 */
export const getUserPermissions = async (id) => {
  try {
    const response = await apiClient.get(ENDPOINTS.USER_PERMISSIONS(id));
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Asignar permisos a un usuario
 */
export const assignUserPermissions = async (id, permissions) => {
  try {
    const response = await apiClient.put(ENDPOINTS.USER_PERMISSIONS(id), {
      permissions: permissions
    });
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Obtener roles disponibles para asignar
 */
export const getAvailableRoles = async () => {
  try {
    const response = await apiClient.get(ENDPOINTS.USER_ROLES);
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Obtener todos los permisos disponibles
 */
export const getAvailablePermissions = async () => {
  try {
    const response = await apiClient.get(ENDPOINTS.ALL_PERMISSIONS);
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Validar email único
 */
export const validateUniqueEmail = async (email, excludeUserId = null) => {
  try {
    const response = await apiClient.post('/users/validar-email', {
      email: email,
      exclude_user_id: excludeUserId
    });
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Obtener estadísticas de usuarios
 */
export const getUserStats = async () => {
  try {
    const response = await apiClient.get(ENDPOINTS.USER_STATS);
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

export default {
  getUsers,
  getUserById,
  createUser,
  updateUser,
  deleteUser,
  changeUserStatus,
  changeUserPassword,
  changeMyPassword,
  getUserPermissions,
  assignUserPermissions,
  getAvailableRoles,
  getAvailablePermissions,
  validateUniqueEmail,
  getUserStats
};
